import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  FlatList,
  TextInput,
  Dimensions,
  Modal,
  Platform,
  StatusBar,
  AccessibilityInfo,
  InteractionManager,
  findNodeHandle,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {createDropdownStyles} from '../../../../shared/components/ui/Dropdown/Dropdown.styles';
import {useScreenTheme} from '../../../../shared/hooks/useScreenTheme';
import {
  DropdownOption,
  DropdownProps,
} from '../../../../shared/components/ui/Dropdown/DropdownProps';
import DropdownArrowIcon from '../../../../shared/assets/icons/DropdownArrowIcon';
import DropdownTickMark from '../../../../shared/assets/icons/DropdownTickMark';
import SearchIcon from '../../../../shared/assets/icons/SearchIcon';
import SearchArrowIcon from '../../../../shared/assets/icons/SearchArrowIcon';
import CloseIcon from '../../../../shared/assets/icons/CloseIcon';
import {accessibiltyText} from '../../../../shared/constants/global';
import {useAccessibilityProps} from '../../../../shared/hooks/useAccessibilityProps';
import Typography from '../../../constants/Typography';
import LargeCloseIcon from '@shared/assets/icons/LargeCloseIcon';
import SearchResult from '@shared/assets/icons/SearchResult';

// ─── Per-item hover wrapper ──────────────────────────────────────────────────
interface DropdownMenuItemProps {
  item: DropdownOption;
  isSelected: boolean;
  /** True when this item is the last one visible in the dropdown viewport
   *  AND the panel opens below the trigger.
   *  Drives borderBottomLeftRadius / borderBottomRightRadius on Android where
   *  overflow:'hidden' is not available (it removes elevation shadow). */
  isLast: boolean;
  /** True when this item is the first one visible in the dropdown viewport
   *  AND the panel opens above the trigger.
   *  Drives borderTopLeftRadius / borderTopRightRadius on Android. */
  isFirst: boolean;
  styles: ReturnType<typeof createDropdownStyles>;
  theme: any;
  showTickMark: boolean;
  formatTextData: boolean;
  onSelect: (item: DropdownOption) => void;
  t: (key: string) => string;
  showSearch: boolean;
}

const DropdownMenuItem = memo(
  ({
    item,
    isSelected,
    isLast,
    isFirst,
    styles,
    theme,
    showTickMark,
    formatTextData,
    onSelect,
    t,
    showSearch
  }: DropdownMenuItemProps) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <TouchableOpacity
        style={[
          styles.menuItem,
          isSelected && styles.activeItem,
          isHovered && !isSelected && styles.menuItemHover,
          isLast && !showSearch && styles.menuItemLast,
          isFirst && !showSearch && styles.menuItemFirst,
        ]}
        onPress={() => {
          onSelect(item);
          AccessibilityInfo.announceForAccessibility(
            `${item.label} ${t(
              accessibiltyText.dropdown.itemSelectedAnnouncementKey,
            )}`,
          );
        }}
        accessible={true}
        accessibilityRole="menuitem"
        accessibilityLabel={
          item.accessibilityLabel ||
          (isSelected
            ? `${item.label}, ${t(accessibiltyText.dropdown.itemSelectedKey)}`
            : `${item.label}, ${t(
                accessibiltyText.dropdown.itemNotSelectedKey,
              )}`)
        }
        accessibilityState={{selected: isSelected}}
        // @ts-ignore — onMouseEnter/onMouseLeave are valid on React Native Web
        onMouseEnter={() => setIsHovered(true)}
        // @ts-ignore
        onMouseLeave={() => setIsHovered(false)}
        // @ts-ignore — tabIndex / focusable are required for Tab-key focus on web
        tabIndex={0}
        focusable={true}>
        <View
          style={[
            styles.dropdownItemContainer,
            {flex: 1},
          ]}>
          <Text
            style={[styles.menuItemText, isSelected && styles.activeItemText]}>
            {item.label}
          </Text>
          {item?.desc && item?.desc !== '' && (
            <Text
              style={[styles.descText, isSelected && styles.activeItemText]}>
              {item.desc}
            </Text>
          )}
        </View>
        {isSelected && showTickMark && (
          <View style={styles.tickMark}>
            <DropdownTickMark color={theme.selectedDropdownTextColor} />
          </View>
        )}
      </TouchableOpacity>
    );
  },
);

const ITEM_HEIGHT = Typography.height.height50;
const MAX_ITEMS_VISIBLE = 5;
const SEARCH_ROW_HEIGHT = 50;
/** Vertical padding applied inside the dropdownValues ScrollView.
 *  Top uses padding10 (tight fit at the top); bottom uses padding16 to give
 *  visible breathing room after the last item.  Both values must match the
 *  actual paddingTop / paddingBottom set on `styles.dropdownValues` below. */
const DROPDOWN_LIST_PADDING =
  Typography.padding.padding10 + Typography.padding.padding16;

const Dropdown: React.FC<DropdownProps> = ({
  label,
  customTextLabelStyles,
  selectedSort,
  options,
  onSelect,
  customTextStyles,
  customContainerStyles,
  image,
  inputRef,
  customIcon,
  placeholder,
  title,
  showTickMark = false,
  showLabelWithSelected = false,
  customLabelStyles,
  accessibilityLabel,
  accessibilityHint,
  accessibilityRole = 'combobox',
  accessibilityState,
  accessible = true,
  testID,
  isDisabled = false,
  hasHeightGap = false,
  isMandatory = false,
  showSearch = false,
  searchPlaceholder,
  emptyMessage,
  numberOfLines,
  showSearchArrow = false,
  formatTextData = false,
  filterByLabelOnly = false,
}) => {
  const {t} = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [searchText, setSearchText] = useState('');
  const searchInputRef = useRef<TextInput>(null);
  /**
   * Ref for the first result item (or the empty message) so that after the
   * "Submit search" arrow button is pressed, accessibility focus jumps to the
   * first result instead of staying on the search input.
   */
  const firstResultRef = useRef<React.ElementRef<typeof TouchableOpacity> | null>(null);
  const emptyMessageRef = useRef<React.ElementRef<typeof View> | null>(null);
  /**
   * Filtered options stored in state so they can be updated inside a React
   * transition — keeping the TextInput update (high-priority, instant) separate
   * from the list re-render (low-priority, interruptible).
   */
  const [filteredOptions, setFilteredOptions] = useState<DropdownOption[]>(
    () => options,
  );
  /**
   * Internal ref for the trigger TouchableOpacity so we can always return
   * focus to it after the dropdown closes — regardless of whether the caller
   * also passes an `inputRef`.
   */
  const triggerRef = useRef<React.ElementRef<typeof TouchableOpacity> | null>(
    null,
  );

  /**
   * Moves accessibility focus to the first result item or empty message after
   * the search arrow is submitted.
   */
  const focusFirstResult = useCallback(() => {
    InteractionManager.runAfterInteractions(() => {
      if (Platform.OS === 'web') {
        const node = (firstResultRef.current ?? emptyMessageRef.current) as any;
        if (node && typeof node.focus === 'function') {
          node.focus();
        }
      } else {
        const target = firstResultRef.current ?? emptyMessageRef.current;
        const handle = findNodeHandle(target);
        if (handle != null) {
          AccessibilityInfo.setAccessibilityFocus(handle);
        }
      }
    });
  }, []);

  /**
   * Returns accessibility focus to the dropdown trigger after the modal has
   * fully dismissed.  Uses InteractionManager so the focus call happens after
   * all pending React Native animations / layout work completes.
   */
  const restoreFocusToTrigger = useCallback(() => {
    InteractionManager.runAfterInteractions(() => {
      if (Platform.OS === 'web') {
        // On web, use the raw DOM focus() API.
        const node = triggerRef.current as any;
        if (node) {
          const domNode =
            typeof node.focus === 'function'
              ? node
              : node._nativeTag
              ? document.querySelector(`[data-focusable="true"]`)
              : null;
          if (domNode && typeof domNode.focus === 'function') {
            domNode.focus();
          }
        }
      } else {
        // On native (iOS / Android), use AccessibilityInfo.setAccessibilityFocus.
        const handle = findNodeHandle(triggerRef.current);
        if (handle != null) {
          AccessibilityInfo.setAccessibilityFocus(handle);
        }
      }
    });
  }, []);

  /** Close the dropdown and reset any active search text. */
  const closeDropdown = useCallback(
    (restoreFocus = false) => {
      setIsOpen(false);
      setSearchText('');
      // Reset the list to the full options so the next open is unfiltered.
      setFilteredOptions(options);
      // Web only: reset so the next open starts hidden until re-measured
      if (Platform.OS === 'web') {
        setIsPositioned(false);
      }
      if (restoreFocus) {
        restoreFocusToTrigger();
      }
    },
    [restoreFocusToTrigger, options],
  );

  /**
   * Filters options by the given text and updates state.  React 18
   * auto-batches this with the companion setSearchText call in
   * handleSearchChange, so only one re-render fires per keystroke.
   */
  const applyFilter = useCallback(
    (text: string) => {
      if (!showSearch || !text.trim()) {
        setFilteredOptions(options);
        return;
      }
      const lower = text.toLowerCase();
      setFilteredOptions(
        options.filter(opt => {
          if ((opt.label ?? '').toLowerCase().includes(lower)) {
            return true;
          }
          if (!filterByLabelOnly && opt.desc) {
            return opt.desc.toLowerCase().includes(lower);
          }
          return false;
        }),
      );
    },
    [options, showSearch, filterByLabelOnly],
  );

  /**
   * Handles TextInput changes: updates the visible text at high priority and
   * schedules the list filter as a low-priority transition.
   */
  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchText(text);
      applyFilter(text);
    },
    [applyFilter],
  );

  /** Index of the first item currently visible in the FlatList viewport.
   *  Used when the panel opens above the trigger. */
  const [firstVisibleIndex, setFirstVisibleIndex] = useState<number>(0);

  /** Index of the last item currently visible in the FlatList viewport.
   *  Initialised to the last data item (correct for short lists that don't
   *  scroll), then updated via onViewableItemsChanged as the user scrolls. */
  const [lastVisibleIndex, setLastVisibleIndex] = useState<number>(
    () => Math.max(0, filteredOptions.length - 1),
  );

  /** Keep lastVisibleIndex in sync when the options list changes (e.g. search). */
  useEffect(() => {
    setLastVisibleIndex(Math.max(0, filteredOptions.length - 1));
  }, [filteredOptions.length]);

  /** Stable callback — FlatList requires onViewableItemsChanged not to change
   *  reference after mount; empty deps array satisfies that contract. */
  const handleViewableItemsChanged = useCallback(
    ({viewableItems}: {viewableItems: Array<{index: number | null; isViewable: boolean}>}) => {
      const visible = viewableItems.filter(vi => vi.isViewable);
      const first = visible[0];
      const last = visible[visible.length - 1];
      if (first !== undefined && first.index !== null && first.index !== undefined) {
        setFirstVisibleIndex(first.index);
      }
      if (last !== undefined && last.index !== null && last.index !== undefined) {
        setLastVisibleIndex(last.index);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /** Must be a stable ref — FlatList reads viewabilityConfig only once. */
  const viewabilityConfig = useRef({viewAreaCoveragePercentThreshold: 50});

  const [lastSelected, setLastSelected] = useState<string | undefined>(
    selectedSort?.label,
  );

  /**
   * Ref tracking the latest searchText for effects that should not re-run on
   * every keystroke (e.g. the options-sync effect below).
   */
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;

  /**
   * Re-apply the current filter whenever options changes (e.g. after the async
   * technician fetch completes) so the list stays consistent.
   */
  useEffect(() => {
    applyFilter(searchTextRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  /**
   * Compute a uniform width from the **average** character count of all option
   * labels.  Both the trigger button (selected-value box) and the dropdown
   * panel receive this same `minWidth` so their widths stay visually consistent
   * regardless of which value is currently selected.  Labels wider than this
   * value wrap naturally to additional lines inside the panel instead of being
   * truncated with an ellipsis.
   */
  const averageLabelWidth = useMemo(() => {
    if (options.length === 0) {
      return 0;
    }
    const totalLength = options.reduce(
      (sum, o) => sum + (o.label ?? '').length,
      0,
    );
    const avg = totalLength / options.length;
    // 8 px per average character + 32 px for horizontal padding / icon room.
    return Math.round(avg * 8 + 32);
  }, [options]);

  /**
   * Maximum height of the options ScrollView, accounting for the list's
   * vertical padding and the gap between items so no item is ever clipped.
   */
  const dropdownListMaxHeight = useMemo(() => {
    const n = Math.min(filteredOptions.length, MAX_ITEMS_VISIBLE);
    return (
      n * ITEM_HEIGHT +
      Math.max(0, n - 1) * Typography.gap.gap16 +
      DROPDOWN_LIST_PADDING
    );
  }, [filteredOptions.length]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        // Restore focus to trigger on Escape so the cursor stays on the dropdown.
        closeDropdown(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Keep lastSelected in sync when selectedSort is controlled externally.
  // Always update (including when selectedSort becomes undefined) so the
  // accessibility label is cleared when filters are reset.
  useEffect(() => {
    setLastSelected(selectedSort?.label);
  }, [selectedSort?.label]);

  const theme = useScreenTheme('dropdown');
  const styles = useMemo(() => createDropdownStyles(theme), [theme]);
  // Build the trigger's accessible label, including the currently selected value
  // so screen readers announce e.g. "Organization, required, Active Fleet" after selection.
  const triggerBaseLabel =
    accessibilityLabel ||
    title ||
    placeholder ||
    t(accessibiltyText.dropdown.accessibilityLabelKey);

  // Always append the selected value so screen readers announce the current
  // selection regardless of whether an explicit `accessibilityLabel` was supplied.
  const triggerA11yLabel = lastSelected
    ? `${triggerBaseLabel}, ${lastSelected}`
    : triggerBaseLabel;

  const a11yProps = useAccessibilityProps({
    accessibilityLabel: triggerA11yLabel,
    accessibilityHint:
      accessibilityHint || t(accessibiltyText.dropdown.accessibilityHintKey),
  });

  const [dropdownPos, setDropdownPos] = useState<{
    /** Absolute top when showing below the trigger; undefined when above. */
    top: number | undefined;
    /** Absolute bottom (from viewport bottom) when showing above the trigger;
     *  undefined when below.  Anchoring by bottom means the panel's bottom
     *  edge is always flush with the trigger regardless of actual panel height,
     *  so there is never a visible gap between the list and the trigger. */
    bottom: number | undefined;
    left: number | undefined;
    right: number | undefined;
    width: number;
    maxWidth?: number;
    /** Available vertical space on the chosen side.
     *  Used to cap the ScrollView when there are more than MAX_ITEMS_VISIBLE
     *  items and the panel would otherwise overflow the viewport. */
    panelMaxHeight?: number;
  }>({
    top: 0,
    bottom: undefined,
    left: undefined,
    right: undefined,
    width: 0,
  });

  /**
   * Web only — true once measureInWindow has returned valid coordinates.
   * Keeps the dropdown panel invisible (opacity 0) on its very first render
   * so it never flashes at position (0, 0) before the measurement completes.
   * Always true on native so there is zero behaviour change on mobile.
   */
  const [isPositioned, setIsPositioned] = useState(Platform.OS !== 'web');

  const handleSelect = useCallback(
    (option: DropdownOption) => {
      onSelect(option);
      setLastSelected(option.label);
      // Pass restoreFocus=true so that after selection the screen-reader cursor
      // stays on the dropdown trigger rather than jumping to an unpredictable element.
      closeDropdown(true);
    },
    [onSelect, closeDropdown],
  );

  /**
   * Measures the trigger element and computes the dropdown panel position.
   * On web the caller should set `isOpen` independently (panel is kept invisible
   * via `isPositioned` until the callback fires).  On native, pass an `onReady`
   * callback that is invoked *after* `setDropdownPos` so the Modal is only
   * mounted once the coordinates are known — preventing the top-of-screen flash.
   */
  const measureDropdown = useCallback(
    (onReady?: () => void) => {
      // Use the caller-supplied inputRef when available; fall back to the
      // internal triggerRef so positioning always works even without inputRef.
      const nodeToMeasure: {measureInWindow: Function} | null =
        inputRef?.current ?? (triggerRef.current as any) ?? null;
      if (nodeToMeasure) {
        nodeToMeasure.measureInWindow(
          (x: number, y: number, width: number, height: number) => {
            // On web use window.innerWidth/innerHeight directly — these are always
            // the current visible viewport dimensions and are more reliable than
            // Dimensions.get('window') which can lag after resize.
            const usableWidth =
              Platform.OS === 'web'
                ? window?.innerWidth ?? Dimensions.get('window').width
                : Dimensions.get('window').width;
            const usableHeight =
              Platform.OS === 'web'
                ? window?.innerHeight ?? Dimensions.get('window').height
                : Dimensions.get('window').height;

            const statusBarHeight =
              Platform.OS === 'android' ? StatusBar.currentHeight || 0 : 0;

            const GAP = 6;
            const searchRowExtra =
              showSearch && options.length > 0 ? SEARCH_ROW_HEIGHT : 0;

            // Use the same formula as dropdownListMaxHeight (which includes
            // inter-item gaps and vertical padding) so the above/below decision
            // is based on the actual rendered panel height, not a rough estimate.
            const n = Math.min(options.length, MAX_ITEMS_VISIBLE);
            const panelContentHeight =
              n * ITEM_HEIGHT +
              Math.max(0, n - 1) * Typography.gap.gap16 +
              DROPDOWN_LIST_PADDING;
            const dropdownHeight = panelContentHeight + searchRowExtra;

            const spaceBelow = usableHeight - (y + height) - statusBarHeight;
            const spaceAbove = y - statusBarHeight;

            // Available space on each side (after the GAP between trigger and panel).
            const availableBelow = Math.max(0, spaceBelow - GAP);
            const availableAbove = Math.max(0, spaceAbove - GAP);

            // Prefer below; flip above when the full panel doesn't fit below.
            // When showing above we anchor by `bottom` (distance from viewport
            // bottom to the trigger's top edge) so the panel's bottom is always
            // flush with the trigger — no gap caused by an inaccurate height estimate.
            let top: number | undefined;
            let bottom: number | undefined;
            let panelMaxHeight: number;

            if (availableBelow >= dropdownHeight) {
              // Full panel fits below — show below at full height.
              top = y + height + GAP;
              bottom = undefined;
              panelMaxHeight = availableBelow;
            } else if (availableAbove >= dropdownHeight) {
              // Full panel doesn't fit below but fits above — flip above.
              top = undefined;
              bottom =
                Platform?.OS === 'android'
                  ? usableHeight - y + GAP - 58
                  : usableHeight - y + GAP;
              panelMaxHeight = availableAbove;
            } else {
              // Neither side fits — choose the side with more room and scroll.
              if (availableBelow >= availableAbove) {
                top = y + height + GAP;
                bottom = undefined;
                panelMaxHeight = availableBelow;
              } else {
                top = undefined;
                bottom = usableHeight - y + GAP;
                panelMaxHeight = availableAbove;
              }
            }

            // Horizontal positioning: left-anchor when trigger is in the left half
            // of the viewport; right-anchor when in the right half.  On web, also
            // clamp so the panel never overflows the viewport edges.
            let left: number | undefined;
            let right: number | undefined;

            if (Platform.OS === 'web') {
              const panelLeft = x;
              const panelRight = x + width;
              if (panelRight > usableWidth / 2 && panelLeft > usableWidth / 2) {
                // Trigger is mostly in the right half — right-anchor the panel.
                right = Math.max(0, usableWidth - panelRight);
              } else {
                // Left-anchor and clamp within viewport.
                left = Math.max(
                  0,
                  Math.min(panelLeft, usableWidth - width - 8),
                );
              }
            } else {
              if (x > usableWidth / 2) {
                right = usableWidth - (x + width);
              } else {
                left = x;
              }
            }

            // Compute maxWidth so the panel never overflows the viewport on web.
            const maxWidth =
              Platform.OS === 'web'
                ? left !== undefined
                  ? usableWidth - left - 8
                  : right !== undefined
                  ? usableWidth - right - 8
                  : usableWidth - 16
                : undefined;

            setDropdownPos({
              top,
              bottom,
              left,
              right,
              width,
              maxWidth,
              panelMaxHeight,
            });

            if (Platform.OS === 'web') {
              // Web: reveal the panel now that it has correct coordinates.
              setIsPositioned(true);
            } else {
              // Native: invoke the caller's callback so the Modal mounts only
              // after the position is known — no top-of-screen flash.
              onReady?.();
            }
          },
        );
      } else {
        // No measurable node — open immediately as a safe fallback.
        onReady?.();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [inputRef, showSearch, options.length],
  );

  const handleOrientationChange = useCallback(() => {
    if (isOpen) {
      measureDropdown();
    }
  }, [isOpen, measureDropdown]);

  /** Memoised trigger-press handler — stable reference avoids re-renders. */
  const handleTriggerPress = useCallback(() => {
    if (isOpen) {
      closeDropdown();
    } else if (Platform.OS === 'web') {
      measureDropdown();
      setIsOpen(true);
    } else {
      measureDropdown(() => setIsOpen(true));
    }
  }, [isOpen, closeDropdown, measureDropdown]);

  /**
   * True when the panel opens below the trigger (top is set); false when above
   * (bottom is set).  Drives the dynamic corner-radius logic so the panel and
   * trigger always share a seamless flat edge where they meet.
   */
  const showsBelow = dropdownPos.top !== undefined;

  /** Memoised FlatList renderItem — stable across renders. */
  const renderItem = useCallback(
    ({item, index}: {item: DropdownOption; index: number}) => {
      // For non-scrollable panels (all items fit without scrolling), every item
      // is always visible so last/first index is purely data-length-based.
      // onViewableItemsChanged can fire with only a partial set on the first
      // render (especially on web), which would set lastVisibleIndex to an
      // incorrect intermediate value like 0 — causing the first item to receive
      // menuItemLast styling by mistake. Using the static data length avoids
      // this entirely for panels that never scroll.
      const isScrollable = filteredOptions.length > MAX_ITEMS_VISIBLE;
      const trueLastIndex = isScrollable
        ? lastVisibleIndex
        : filteredOptions.length - 1;
      const trueFirstIndex = isScrollable ? firstVisibleIndex : 0;
      return (
        <DropdownMenuItem
          item={item}
          isSelected={selectedSort?.value === item.value}
          // Panel below → round the bottom of the last visible item.
          isLast={showsBelow && index === trueLastIndex}
          // Panel above → round the top of the first visible item.
          isFirst={!showsBelow && index === trueFirstIndex}
          styles={styles}
          theme={theme}
          showTickMark={showTickMark}
          formatTextData={formatTextData}
          onSelect={handleSelect}
          t={t}
          showSearch={showSearch}
        />
      );
    },
    [
      styles,
      theme,
      selectedSort,
      handleSelect,
      formatTextData,
      showTickMark,
      t,
      lastVisibleIndex,
      firstVisibleIndex,
      filteredOptions.length,
      showsBelow,
    ],
  );

  /**
   * Key extractor for FlatList.  Appending the index ensures uniqueness even
   * when the options array contains items with duplicate `value` fields
   * (which would otherwise cause React reconciliation errors).
   */
  const keyExtractor = useCallback(
    (item: DropdownOption, index: number) => `${item.value}_${index}`,
    [],
  );

  return (
    <View
      style={[styles.innerContainer, isDisabled && {pointerEvents: 'none'}]}>
      {title && (
        <Text style={[styles.label, customTextLabelStyles]}>
          {title}
          {isMandatory && <Text style={styles.mandatoryMark}>{' *'}</Text>}
        </Text>
      )}

      <TouchableOpacity
        ref={node => {
          // Keep both the internal triggerRef and the caller-supplied inputRef in sync.
          triggerRef.current = node;
          if (inputRef) {
            (inputRef as React.MutableRefObject<any>).current = node;
          }
        }}
        style={[
          styles.input,
          // Zero the touching edge so trigger and panel share a flat seam.
          isOpen &&
            (showsBelow ? styles.dropdownOpen : styles.dropdownOpenAbove),
          !isOpen && isHovered && styles.hoverInput,
          isOpen && styles.openInput,
          isDisabled && styles.disabledInput,
          // Match the trigger width to the dropdown panel so the selected-value
          // box never appears narrower than the open list.
          averageLabelWidth > 0 ? {minWidth: averageLabelWidth} : undefined,
          customContainerStyles,
        ]}
        // @ts-ignore — web only hover events
        onMouseEnter={() => Platform.OS === 'web' && setIsHovered(true)}
        onMouseLeave={() => Platform.OS === 'web' && setIsHovered(false)}
        onPress={isDisabled ? undefined : handleTriggerPress}
        disabled={isDisabled}
        activeOpacity={0.8}
        testID={testID}
        accessible={accessible}
        accessibilityRole={accessibilityRole}
        {...a11yProps}
        accessibilityValue={lastSelected ? {text: lastSelected} : undefined}
        accessibilityState={{
          expanded: isOpen,
          disabled: isDisabled,
          ...accessibilityState,
        }}
        // @ts-ignore — tabIndex and focusable are required for Tab-key focus on web
        tabIndex={0}
        focusable={true}>
        {image}
        {showLabelWithSelected ? (
          <View
            style={[
              styles.showLabelWithSelected,
              numberOfLines != null && {flex: 1},
            ]}>
            <Text
              style={[styles.showlabelText, customLabelStyles]}
              numberOfLines={numberOfLines}
              ellipsizeMode={numberOfLines != null ? 'tail' : undefined}>
              {label || selectedSort?.label || placeholder || ''}
            </Text>
            <Text
              style={[
                !selectedSort?.label
                  ? styles.placeHodlerInputText
                  : styles.inputText,
                customTextStyles,
              ]}
              numberOfLines={numberOfLines}
              ellipsizeMode={numberOfLines != null ? 'tail' : undefined}>
              {selectedSort?.label || placeholder || ''}
            </Text>
          </View>
        ) : (
          <View
            style={[
              styles.showLabelWithSelected,
              numberOfLines != null && {flex: 1},
            ]}>
            <Text
              style={[
                !selectedSort?.label
                  ? styles.placeHodlerInputText
                  : styles.inputText,
                customTextStyles,
              ]}
              numberOfLines={numberOfLines}
              ellipsizeMode={numberOfLines != null ? 'tail' : undefined}>
              {label || selectedSort?.label || placeholder || ''}
            </Text>
          </View>
        )}

        {customIcon ? (
          customIcon
        ) : (
          <DropdownArrowIcon color={(theme as any).dropdownIcon} />
        )}
      </TouchableOpacity>

      {isOpen && (
        <Modal
          transparent
          visible
          animationType="none"
          supportedOrientations={[
            'portrait',
            'landscape',
            'landscape-left',
            'landscape-right',
          ]}
          onOrientationChange={handleOrientationChange}
          accessibilityViewIsModal={true}>
          {/* Overlay — tap outside to close */}
          <TouchableWithoutFeedback onPress={() => closeDropdown()}>
            <View style={styles.modalOverlay} accessible={false} />
          </TouchableWithoutFeedback>

          {/* Dropdown panel — hidden on web until measureInWindow returns */}
          <View
            style={[
              showSearch ? styles.dropdownWithSearchMenu : styles.dropdownMenu,
              // Zero the touching edge of the panel so it meets the trigger flush.
              showsBelow ? styles.panelOpenBelow : styles.panelOpenAbove,
              {
                top: dropdownPos.top,
                bottom: dropdownPos.bottom,
                left: dropdownPos.left,
                right: dropdownPos.right,
                // Apply the average-based width to the panel so it always
                // matches the trigger button width.  Labels wider than this
                // computed value wrap to additional lines rather than being
                // clipped by an ellipsis.
                ...(averageLabelWidth > 0
                  ? {
                      width:
                        Math.max(dropdownPos.width || 0, averageLabelWidth) ||
                        undefined,
                    }
                  : {minWidth: dropdownPos.width || undefined}),
                ...(dropdownPos.maxWidth !== undefined
                  ? {maxWidth: dropdownPos.maxWidth}
                  : {}),
                // Cap the entire panel (trigger + search row + list) to the
                // available space on the chosen side so it never overflows the
                // viewport boundary regardless of how many items are in the list.
                ...(dropdownPos.panelMaxHeight !== undefined
                  ? {maxHeight: dropdownPos.panelMaxHeight}
                  : {}),
                opacity: isPositioned ? 1 : 0,
              },
            ]}>
            {/* ── Search row (only when showSearch is true AND there are options) ── */}
            {showSearch && options.length > 0 && (
              <View style={styles.searchRow}>
                <TouchableOpacity
                  style={styles.searchIconWrapper}
                  onPress={() => searchInputRef.current?.focus()}
                  accessibilityRole="button"
                  accessibilityLabel={searchPlaceholder ?? 'Search'}>
                  <SearchIcon
                    color={(theme as any).dropdownTextColor}
                    borderColor={(theme as any).searchBorder}
                  />
                </TouchableOpacity>
                <TextInput
                  ref={searchInputRef}
                  style={[
                    styles.searchTextInput,
                    Platform.OS === 'web' && ({outline: 'none'} as any),
                  ]}
                  value={searchText}
                  onChangeText={text => {
                    setSearchText(text);
                    if(showSearchArrow) {
                      if (text === '') {
                        applyFilter('');
                      }
                    } else {
                      applyFilter(text);
                    }
                  }}
                  placeholder={searchPlaceholder ?? 'Search...'}
                  placeholderTextColor={
                    (theme as any).workOrderDropdownPlaceholderText
                  }
                  autoCorrect={false}
                  autoCapitalize="none"
                  // On web, auto-focus so the user can type immediately without
                  // having to click the field first.
                  autoFocus={Platform.OS === 'web'}
                  accessibilityLabel={searchPlaceholder ?? 'Search'}
                  testID="dropdown-search-input"
                />
                {searchText !== '' && (
                  <TouchableOpacity
                    style={styles.closeIcon}
                    accessibilityRole="button"
                    accessibilityLabel="Clear search"
                    onPress={() => {
                      setSearchText('');
                      applyFilter('');
                      searchInputRef.current?.focus();
                    }}>
                    <LargeCloseIcon
                      width={16}
                      height={16}
                      color={(theme as any).dropdownTextColor}
                    />
                  </TouchableOpacity>
                )}
                {showSearchArrow && (
                  <TouchableOpacity
                    style={[styles.searchArrowBtn]}
                    accessibilityRole="button"
                    accessibilityLabel="Submit search"
                    // @ts-ignore — tabIndex / focusable required for Tab-key focus on web
                    tabIndex={0}
                    focusable={true}
                    onPress={() => {
                      applyFilter(searchText);
                      // Move focus to the first result (or empty message) so the
                      // user can immediately Tab/swipe through results after submitting.
                      focusFirstResult();
                    }}>
                    <SearchArrowIcon
                      color={(theme as any).inputColor}
                      bgColor={theme.searchResultBGColor}
                      borderColor={theme.searchBorder}
                    />
                  </TouchableOpacity>
                )}
              </View>
            )}

            {/* ── Empty message (no options or search yields nothing) ── */}
            {filteredOptions.length === 0 && emptyMessage ? (
              <View
                ref={emptyMessageRef}
                style={
                  showSearch
                    ? styles.emptyMessageSearchContainer
                    : styles.emptyMessageContainer
                }
                accessible={true}
                accessibilityRole="text"
                accessibilityLabel={emptyMessage}
                accessibilityLiveRegion="polite"
                // @ts-ignore — tabIndex / focusable required for Tab-key focus on web
                tabIndex={0}
                focusable={true}>
                <Text style={styles.emptyMessageText} accessible={false}>
                  {emptyMessage}
                </Text>
              </View>
            ) : (
              /* ── Options list — FlatList virtualises so only visible rows are
                 rendered regardless of list size (handles 200+ technicians). ── */
              <FlatList
                data={filteredOptions}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                bounces={false}
                // extraData forces FlatList to re-render when search text,
                // selection, or last-visible-index changes so the bottom-radius
                // item and active highlight are always correct.
                extraData={[searchText, lastVisibleIndex, firstVisibleIndex, showsBelow]}
                onViewableItemsChanged={handleViewableItemsChanged}
                viewabilityConfig={viewabilityConfig.current}
                initialNumToRender={MAX_ITEMS_VISIBLE + 2}
                maxToRenderPerBatch={MAX_ITEMS_VISIBLE + 2}
                windowSize={3}
                showsVerticalScrollIndicator={
                  filteredOptions.length > MAX_ITEMS_VISIBLE
                }
                style={[
                  showSearch
                    ? styles.dropdownSearchValues
                    : styles.dropdownValues,
                  {
                    // Always cap the list height to fit within the available
                    // viewport space on the chosen side.  This prevents the panel
                    // from overflowing the screen boundary both when there are
                    // more than MAX_ITEMS_VISIBLE items AND when the dropdown is
                    // positioned near a screen edge with fewer items.
                    maxHeight: (() => {
                      const searchExtra =
                        showSearch && options.length > 0
                          ? SEARCH_ROW_HEIGHT
                          : 0;
                      // Natural height: what the full (capped) list would need.
                      const naturalHeight = dropdownListMaxHeight;
                      // Available height on the chosen side minus the search row.
                      const available = Math.max(
                        ITEM_HEIGHT + DROPDOWN_LIST_PADDING,
                        (dropdownPos.panelMaxHeight ?? naturalHeight) -
                          searchExtra,
                      );
                      return Math.min(naturalHeight, available);
                    })(),
                  },
                ]}
                keyboardShouldPersistTaps="handled"
              />
            )}
          </View>
        </Modal>
      )}
    </View>
  );
};

export default Dropdown;
